import React, { useCallback, useEffect, useMemo, useState } from 'react'

import {
  Button,
  Flex,
  Header,
  Icon,
  IconButton,
  InputText,
  Paper,
  Table,
  TableColumn,
  Text,
  Tooltip,
  UserAvatar,
  useTableRef,
} from '@driftt/dds'
import { faEye } from '@driftt/dds.icons/light/faEye'
import { faSparkles } from '@driftt/dds.icons/solid/faSparkles'

import useCutover from 'hooks/useCutover'
import useDebounce from 'hooks/useDebounce'
import { isNilOrEmpty } from 'utils/ramda'

import { DetailsDrawer } from '../DetailsDrawer'
import {
  useSiteConciergeSearchCSVExport,
  useSiteConciergeSearchTable,
} from '../Hooks'
import { SiteConciergeSearchTableMetricsRow } from '../types'

export const SearchAppChartTable = () => {
  const tableRef = useTableRef()
  const { hasCutover, hasFetchedCutover } = useCutover(
    'SC_SEARCH_APP_REPORTING_DETAILS'
  )

  const [searchKey, setSearchKey] = useState<string>('')

  const [selectedSearchPreview, setSelectedSearchPreview] =
    useState<SiteConciergeSearchTableMetricsRow | null>(null)
  const [currentSearchIndex, setCurrentSearchIndex] = useState<number | null>(
    null
  )

  const debouncedSearchText = useDebounce(searchKey, 700)
  const isDrawerOpen = !isNilOrEmpty(selectedSearchPreview)

  const {
    tableData,
    totalRowCount,
    handleUpdateTableState,
    pagination,
    isLoading,
    isError,
  } = useSiteConciergeSearchTable({
    searchFilter: debouncedSearchText,
  })

  const { isDownloadingExport, exportCubeData } =
    useSiteConciergeSearchCSVExport(debouncedSearchText)

  const handleExportToCSV = useCallback(() => {
    exportCubeData()
  }, [exportCubeData])

  useEffect(() => {
    if (isNilOrEmpty(currentSearchIndex)) {
      setSelectedSearchPreview(null)
    } else if (!isNilOrEmpty(tableData?.[currentSearchIndex])) {
      setSelectedSearchPreview(tableData[currentSearchIndex])
    }
  }, [currentSearchIndex, tableData])

  const navigatePagination = (increment: number) => {
    const newSearchIndex = isNilOrEmpty(currentSearchIndex)
      ? 0
      : currentSearchIndex + increment
    const totalPages = totalRowCount
      ? Math.ceil(totalRowCount / pagination.pageSize)
      : 0

    if (newSearchIndex >= 0 && newSearchIndex < tableData.length) {
      setCurrentSearchIndex(newSearchIndex)
    } else if (
      pagination.pageIndex + increment >= 0 &&
      pagination.pageIndex + increment < totalPages
    ) {
      tableRef.current?.reset({
        pagination: {
          pageIndex: pagination.pageIndex + increment,
          pageSize: pagination.pageSize,
        },
      })
      setCurrentSearchIndex(increment > 0 ? 0 : pagination.pageSize - 1)
    }
  }

  const defaultColumns = useMemo(
    () => [
      {
        header: 'Query',
        value: 'query',
        cell: ({ query, isAiGenerated }) => (
          <Flex width={265} alignItems="center" gap="half">
            {isAiGenerated ? (
              <Tooltip
                content="The visitor searched with a generated question"
                side="top"
              >
                <Icon color="purple7" icon={faSparkles} />
              </Tooltip>
            ) : null}
            <Text truncate>{query}</Text>
          </Flex>
        ),
        size: 265,
        disableSort: true,
      },
      {
        header: 'Result',
        value: 'queryResults',
        cell: ({ queryResults }) => (
          <Text css={{ maxWidth: 280 }} truncate>
            {queryResults}
          </Text>
        ),
        size: 280,
        disableSort: true,
      },
      {
        header: 'Visitor',
        value: 'visitor',
        cell: ({ visitor }) => (
          <Flex width={152} alignItems="center">
            <UserAvatar size={'small'} name={visitor} imageUrl={''} />
            <Text truncate>{visitor}</Text>
          </Flex>
        ),
        size: 152,
        disableSort: true,
      },
      {
        header: 'Company',
        value: 'company',
        cell: ({ company }) => (
          <Text css={{ maxWidth: 150 }} truncate>
            {company}
          </Text>
        ),
        size: 150,
        disableSort: true,
      },
      {
        header: 'Date',
        value: 'date',
        size: 180,
        cell: ({ date }) => (
          <Text
            css={{
              whiteSpace: 'nowrap',
            }}
          >
            {date}
          </Text>
        ),
      },
    ],
    []
  )

  // TODO: once the details work is done, remove the temporaryDefaultColumns
  const temporaryDefaultColumns =
    hasFetchedCutover && !hasCutover
      ? defaultColumns
      : [
          {
            id: 'messageId',
            value: 'messageId',
            header: '',
            cell: (data) => (
              <IconButton
                title="Review"
                icon={faEye}
                size="small"
                onClick={() => {
                  setSelectedSearchPreview(data)
                  setCurrentSearchIndex(
                    tableData.findIndex(
                      (search) => search.messageId === data.messageId
                    )
                  )
                }}
              />
            ),
            size: 50,
            rootprops: { id: 'search-detail-preview-btn' },
          },
          ...defaultColumns,
        ]

  useEffect(() => {
    const toggleWidget = (isDrawerOpen: boolean) => {
      if (isDrawerOpen) {
        window.drift && window.drift.hide()
      } else {
        window.drift && window.drift.show()
      }
    }
    toggleWidget(isDrawerOpen)
  }, [isDrawerOpen])

  return (
    <Paper
      fullWidth
      elevation={2}
      as={Flex}
      gap={2}
      direction="column"
      alignItems="stretch"
    >
      <Flex
        direction="column"
        gap={2}
        css={{
          padding: '16px 16px 0',
        }}
      >
        <Header variant="h3">All searches</Header>
        <Flex fullWidth justifyContent="space-between" alignItems="center">
          <InputText
            hideLabel
            disabled={isLoading}
            label="Search queries and results"
            value={searchKey}
            onChange={(e) => setSearchKey(e.target.value)}
            placeholder="Search queries and results"
          />
          <Button
            variant="secondary"
            loading={isDownloadingExport}
            disabled={isDownloadingExport || isLoading}
            onClick={handleExportToCSV}
          >
            Export CSV
          </Button>
        </Flex>
      </Flex>
      <Table<SiteConciergeSearchTableMetricsRow>
        tableRef={tableRef}
        rows={tableData ?? []}
        columns={
          temporaryDefaultColumns as TableColumn<SiteConciergeSearchTableMetricsRow>[]
        }
        isLoading={isLoading}
        disableBoxShadow
        loadingRows={10}
        pagination={{
          rowTotal: totalRowCount ?? 0,
          defaultPagination: pagination,
        }}
        onStateChange={handleUpdateTableState}
        emptyState={
          isError
            ? {
                title: 'Something went wrong',
                subtitle: 'Contact your CSM for assistance',
              }
            : undefined
        }
        highlightedRow={(data) =>
          isDrawerOpen && data?.messageId === selectedSearchPreview?.messageId
        }
      />
      {isDrawerOpen && (
        <DetailsDrawer
          isLoadingTableData={isLoading}
          searches={selectedSearchPreview}
          onClose={() => {
            setCurrentSearchIndex(null)
          }}
          onPrevItem={() => navigatePagination(-1)}
          onNextItem={() => navigatePagination(1)}
        />
      )}
    </Paper>
  )
}
